/**
 * The handles a share picture may print, and whose (B37; owner, 2026-10-20:
 * "sharing should only have the handle of the inviter not invitee
 * available"): in the person's own garden their own profile links (the
 * account's profile_links, each printed as the server says, E7); in
 * someone else's, the garden owner's, as the server sends them to a member
 * (GET /garden-place ownerShare, through useGardenPostcode), never the
 * member's own. Whose garden it is is the one member signal (useOwnerOnly,
 * the server's tendedOwnerOf); until the server has said, none.
 *
 * Who uses it: useShareStory (the share studio's handle and signature) and
 * SeasonReviewPage (the season card's preview handle).
 *
 * @module useShareHandles
 */

import React from 'react';
import { useAuth } from '@/shared/hooks/useAuth';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useOwnerOnly } from '@/shared/hooks/useOwnerOnly';
import { linksOf, type LinkKey } from '@/shared/lib/socialLinks';

/** Whose garden it is, and the handles and address a share in it may print. */
export interface ShareHandles {
  /** True in the person's own garden, once the server has said so: their own handles and address. */
  ownGarden: boolean;
  /** True for a member tending someone else's garden: the owner's handles and address, and no link of the member's. */
  tending: boolean;
  /** The handles offered, in the profile's order: the person's own, or the garden owner's. */
  links: Array<{ key: LinkKey; handle: string }>;
  /** In someone else's garden, the owner's garden address (/@slug), or null; null in the person's own (read their own). */
  ownerSlug: string | null;
}

/**
 * The handles a share in the garden being tended may print, and whose they are.
 *
 * @returns ownGarden, tending, links and ownerSlug.
 */
export function useShareHandles(): ShareHandles {
  const { mysqlUser } = useAuth();
  const place = useGardenPostcode();
  const { known, tending } = useOwnerOnly();
  const ownGarden = known && !tending;
  /** The garden owner's links, as the server sends them to a member; none in the person's own garden. */
  const ownerLinks = place.ownerShare?.links;
  /** The person's own links, as the server read them for their account (profile_links). */
  const ownLinks = mysqlUser?.profile_links;
  const links = React.useMemo(
    () => (ownGarden ? linksOf(ownLinks) : tending ? linksOf(ownerLinks) : []),
    [ownGarden, tending, ownLinks, ownerLinks],
  );
  return { ownGarden, tending, links, ownerSlug: tending ? place.ownerShare?.slug ?? null : null };
}
